import React, { useState } from 'react';
import Markdown from 'markdown-to-jsx';
import '../../../styles/features/incidents/IncidentBanner.css';

const SEVERITY_ORDER = { down: 0, degraded: 1 };
const STATUS_LABELS = {
  investigating: 'Investigating',
  identified:    'Identified',
  monitoring:    'Monitoring',
  resolved:      'Resolved',
};

function IncidentBanner({ incidents = [] }) {
  const [dismissed, setDismissed] = useState(new Set());
  const [expanded, setExpanded]   = useState(new Set());

  const active = incidents
    .filter(i => !i.resolved_at && !dismissed.has(i.id))
    .sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 2) - (SEVERITY_ORDER[b.severity] ?? 2));

  if (active.length === 0) return null;

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const dismiss = (e, id) => {
    e.stopPropagation();
    setDismissed(prev => new Set([...prev, id]));
  };

  return (
    <div className="incident-banner-stack">
      {active.map(incident => {
        const isExpanded = expanded.has(incident.id);
        const severityClass = incident.severity === 'down' ? 'banner-critical' : 'banner-degraded';
        const preview = incident.latest_update?.message || incident.description;

        return (
          <div key={incident.id} className={`incident-banner ${severityClass}`}>
            <div
              className="banner-main"
              onClick={() => toggleExpand(incident.id)}
              role="button"
              aria-expanded={isExpanded}
            >
              <div className="banner-left">
                <span className="banner-dot" />
                <div className="banner-text">
                  <div className="banner-title-row">
                    <strong className="banner-title">{incident.title}</strong>
                    <span className="banner-status-pill">
                      {STATUS_LABELS[incident.status] || incident.status}
                    </span>
                    {incident.source === 'auto' && (
                      <span className="banner-source-pill">Auto-detected</span>
                    )}
                  </div>
                  {!isExpanded && preview && (
                    <p className="banner-preview">
                      <Markdown options={{ disableParsingRawHTML: true }}>{preview}</Markdown>
                    </p>
                  )}
                </div>
              </div>
              <div className="banner-actions">
                <span className="banner-toggle">{isExpanded ? '▲' : '▼'}</span>
                <button
                  className="banner-dismiss"
                  onClick={e => dismiss(e, incident.id)}
                  title="Dismiss"
                  aria-label="Dismiss incident banner"
                >
                  ✕
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="banner-detail">
                {incident.updates && incident.updates.length > 0 ? (
                  <div className="banner-timeline">
                    {[...incident.updates].reverse().map(u => (
                      <div key={u.id} className="banner-update">
                        <div className="banner-update-meta">
                          {u.status_label && (
                            <span className={`status-pill pill-${u.status_label}`}>
                              {STATUS_LABELS[u.status_label] || u.status_label}
                            </span>
                          )}
                          <span className="banner-update-time">
                            {new Date(u.created_at).toLocaleString()}
                          </span>
                          {u.created_by !== 'system' && (
                            <span className="banner-update-author">- {u.created_by}</span>
                          )}
                        </div>
                        <div className="banner-update-body">
                          <Markdown options={{ disableParsingRawHTML: true }}>{u.message}</Markdown>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  preview && (
                    <div className="banner-update-body">
                      <Markdown options={{ disableParsingRawHTML: true }}>{preview}</Markdown>
                    </div>
                  )
                )}

                {incident.endpoints?.length > 0 && (
                  <div className="banner-affected">
                    <span className="banner-affected-label">Affected services:</span>
                    {incident.endpoints.map(ep => (
                      <span key={ep.id} className="banner-service-tag">{ep.name}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default IncidentBanner;
